import { AlertCircle } from 'lucide-react'
import { Card } from '@/components/ui'
import { formatNumber } from '@/lib/utils'
import type { StudioVisitFrequencyRow } from '@/services/clientLifecycleService'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

interface VisitFrequencyChartProps {
  rows: StudioVisitFrequencyRow[]
  loading: boolean
  error: string | null
}

export function VisitFrequencyChart({ rows, loading, error }: VisitFrequencyChartProps) {
  // Незавершённый период даёт неполные числа (в первые дни — втрое меньше
  // визитов на клиента), и без пометки это читается как обвал студии.
  const currentPeriod = rows.find((row) => row.isCurrentPeriod)

  return (
    <Card className="p-3.5">
      <div className="mb-3">
        <h2 className="text-base font-semibold">Частота визитов</h2>
        <p className="text-xs text-muted-foreground">Общий контекст по посещаемости студии за периоды сети</p>
      </div>

      {currentPeriod ? (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 p-2.5 text-xs text-foreground">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
          <span>
            Период <span className="font-medium">{currentPeriod.periodLabel}</span> ещё идёт: прошло{' '}
            {currentPeriod.daysElapsed} из {currentPeriod.daysTotal} дней. Последняя точка будет расти до конца
            периода — сравнивать её с прошлыми ещё рано.
          </span>
        </div>
      ) : null}

      {error ? <p className="text-sm text-error">{error}</p> : null}
      {loading ? <p className="py-8 text-center text-sm text-muted-foreground">Загрузка частоты...</p> : null}
      {!loading && !error && rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">Нет данных по частоте визитов</p>
      ) : null}

      {!loading && !error && rows.length > 0 ? (
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 8, right: 12, left: -16, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.35)" />
              <XAxis dataKey="periodLabel" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} unit="%" />
              {currentPeriod ? (
                <ReferenceArea
                  yAxisId="left"
                  x1={currentPeriod.periodLabel}
                  x2={currentPeriod.periodLabel}
                  fill="rgba(148, 163, 184, 0.25)"
                  label={{ value: 'идёт', position: 'insideTop', fontSize: 10 }}
                />
              ) : null}
              <Tooltip
                formatter={(value: unknown, name) => {
                  if (String(name).includes('%')) return [`${Math.round(Number(value))}%`, name]
                  return [formatNumber(Number(value)), name]
                }}
                labelFormatter={(label) => {
                  const row = rows.find((item) => item.periodLabel === label)
                  if (!row) return label
                  return row.isCurrentPeriod
                    ? `${label} — период идёт (${row.daysElapsed} из ${row.daysTotal} дн.)`
                    : `${label}`
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line yAxisId="left" type="monotone" dataKey="avgVisitsPerActive" name="Визитов на клиента" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 3 }} />
              <Line yAxisId="right" type="monotone" dataKey="pctClients1Visit" name="% с 1 визитом" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
              <Line yAxisId="right" type="monotone" dataKey="pctClients4Plus" name="% с 4+ визитами" stroke="#16a34a" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : null}
    </Card>
  )
}
